/** PlanningTool — Airtime Value Engine.
 *
 *  Audit verdict (Feb 2026):
 *    • OLD: 4-step linear flow with two confusing CTAs ("Heatmap" vs "Budget").
 *      Users had to pick a path before seeing any data.
 *    • OLD: VES / CPTA / LSM had zero inline explanation.
 *    • OLD: Budget required a separate API round-trip per change.
 *
 *  NEW unified flow:
 *    1.  User builds a brief (gender, age, LSM, budget).
 *    2.  One button ("Plan my buy →") fetches ranking + heatmap in parallel.
 *    3.  Heatmap + ranking + budget plan render together on the same page.
 *    4.  Budget is an INTERACTIVE SLIDER that recomputes the plan locally
 *        (greedy algorithm mirroring the backend) — zero extra API calls.
 *    5.  Every metric (VES, CPTA, LSM) has a hover tooltip explaining it.
 */
import { useState, useMemo } from "react";
import { NavBar } from "@/components/NavBar";
import { ConfidenceLabel } from "@/components/ConfidenceLabel";
import { PaywallCard } from "@/components/PaywallCard";
import { api } from "@/lib/api";
import { T } from "@/lib/theme";

const BLUE = "#2563EB";

// ── Glossary tooltips ────────────────────────────────────────────────────────
const GLOSS = {
  ves: "Value Equivalent Score — the audience-fit weight of a slot. Higher VES = more people in your target who'll hear the ad per cedi spent. Formula: target_audience × audience_quality ÷ rate.",
  cpta: "Cost Per Thousand Audience — what you pay to reach 1,000 people in your target. Lower is better. Formula: rate ÷ (target_audience ÷ 1,000).",
  lsm: "Living Standards Measure — a 1-10 wealth proxy used by GeoPoll, Kantar & GSS for West African media planning. LSM 1-3 = lower income, 4-7 = middle, 8-10 = affluent. Most national brands target 5-8.",
  target_audience: "People in your gender/age/LSM brief who are tuned in right now. Drawn from live listener count × demographic mix.",
};

function Glossary({ term, children }) {
  return (
    <span title={GLOSS[term]} style={{ borderBottom: "1px dotted #94A3B8", cursor: "help" }}>
      {children}
    </span>
  );
}

// ── Greedy budget allocator (mirrors backend /planning/budget-simulator) ────
function localBudgetPlan(rankedSlots, budgetGhs) {
  const slots = [...rankedSlots].sort((a, b) => a.cpta_ghs - b.cpta_ghs);
  const SPOTS_PER_STATION = 5;
  let budgetLeft = budgetGhs;
  const plan = [];
  let totalReach = 0;
  for (const slot of slots) {
    const rate = slot.rate_card_ghs;
    if (!rate || budgetLeft < rate) continue;
    const maxSpots = Math.min(SPOTS_PER_STATION, Math.floor(budgetLeft / rate));
    if (maxSpots <= 0) continue;
    const spend = rate * maxSpots;
    const reach = slot.target_audience * maxSpots;
    plan.push({ ...slot, spots: maxSpots, spend_ghs: spend, projected_reach: reach });
    budgetLeft -= spend;
    totalReach += reach;
  }
  return {
    budget_ghs: budgetGhs,
    budget_used_ghs: budgetGhs - budgetLeft,
    budget_remaining_ghs: Math.round(budgetLeft * 100) / 100,
    projected_total_reach: totalReach,
    plan,
  };
}

export default function PlanningTool() {
  const [paywall, setPaywall] = useState(null);
  const [brief, setBrief] = useState({
    gender: "all", age_min: 25, age_max: 44,
    lsm_min: 6, lsm_max: 10, regions: [], budget_ghs: 50000,
  });
  const [ranked, setRanked] = useState([]);
  const [heatmap, setHeatmap] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const inp = { width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, color: T.navy };

  // ── ONE button — fetches ranking + heatmap in parallel ───────────────────
  const runPlan = async () => {
    setLoading(true); setPaywall(null);
    try {
      const params = new URLSearchParams({
        gender: brief.gender,
        age_min: String(brief.age_min), age_max: String(brief.age_max),
        lsm_min: String(brief.lsm_min), lsm_max: String(brief.lsm_max),
      });
      const [r, h] = await Promise.all([
        api.post("/planning/recommend", brief),
        api.get(`/planning/heatmap?${params}`),
      ]);
      setRanked(r.data.ranked_slots || []);
      setHeatmap(h.data.grid || []);
      setHasRun(true);
    } catch (e) {
      if (e.response?.status === 402) {
        const d = e.response.data?.detail || {};
        setPaywall({ needed: d.needed_plan || "pro", current: d.current_plan || "free", message: d.message });
      } else {
        alert(e.response?.data?.detail || "Failed");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Local budget plan — recomputed instantly when slider moves ───────────
  const plan = useMemo(
    () => (ranked.length ? localBudgetPlan(ranked, brief.budget_ghs) : null),
    [ranked, brief.budget_ghs]
  );

  const maxVes = heatmap.length
    ? Math.max(1, ...heatmap.flatMap((row) => row.cells.map((c) => c.ves)))
    : 1;

  return (
    <div style={{ minHeight: "100vh", background: "#fff", fontFamily: "'Inter',system-ui,sans-serif" }} data-testid="planning-page">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap')`}</style>
      <NavBar dark={false} />

      <div style={{ maxWidth: 1240, margin: "32px auto", padding: "0 24px" }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, color: T.navy, marginBottom: 4 }}>Airtime Value Planning Tool</h1>
        <p style={{ color: T.muted, marginBottom: 28, maxWidth: 720, lineHeight: 1.55 }}>
          Tell us your target audience and budget. We&apos;ll show you the best slots ranked by{" "}
          <Glossary term="ves">VES</Glossary>, the lowest{" "}
          <Glossary term="cpta">CPTA</Glossary>, and a complete buy-plan you can adjust live.
        </p>

        {paywall && <PaywallCard needed={paywall.needed} currentPlan={paywall.current} message={paywall.message} />}

        {/* ── BRIEF BUILDER ─────────────────────────────────────────────── */}
        <div style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }} data-testid="brief-builder">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
            <div style={{ fontWeight: 700, color: T.navy, fontSize: 16 }}>1. Define your audience</div>
            <div style={{ color: T.muted, fontSize: 11 }}>
              Pre-filled with a common youth-affluence brief — adjust to match yours.
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 14 }}>
            <div>
              <div style={{ color: T.muted, fontSize: 11, marginBottom: 4 }}>Gender</div>
              <select data-testid="brief-gender" style={inp} value={brief.gender} onChange={(e) => setBrief({ ...brief, gender: e.target.value })}>
                <option value="all">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 11, marginBottom: 4 }}>Age min</div>
              <input data-testid="brief-age-min" type="number" style={inp} value={brief.age_min} onChange={(e) => setBrief({ ...brief, age_min: Number(e.target.value) })} />
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 11, marginBottom: 4 }}>Age max</div>
              <input data-testid="brief-age-max" type="number" style={inp} value={brief.age_max} onChange={(e) => setBrief({ ...brief, age_max: Number(e.target.value) })} />
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 11, marginBottom: 4 }}>
                <Glossary term="lsm">LSM</Glossary> min
              </div>
              <input data-testid="brief-lsm-min" type="number" min={1} max={10} style={inp} value={brief.lsm_min} onChange={(e) => setBrief({ ...brief, lsm_min: Number(e.target.value) })} />
            </div>
            <div>
              <div style={{ color: T.muted, fontSize: 11, marginBottom: 4 }}>
                <Glossary term="lsm">LSM</Glossary> max
              </div>
              <input data-testid="brief-lsm-max" type="number" min={1} max={10} style={inp} value={brief.lsm_max} onChange={(e) => setBrief({ ...brief, lsm_max: Number(e.target.value) })} />
            </div>
          </div>
          <div style={{ marginTop: 18 }}>
            <button data-testid="brief-run-btn" onClick={runPlan} disabled={loading}
              style={{ padding: "12px 28px", background: BLUE, color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 14 }}>
              {loading ? "Computing your media plan…" : hasRun ? "↻ Re-run with new audience" : "Plan my buy →"}
            </button>
          </div>
        </div>

        {hasRun && (
          <>
            {/* ── BUDGET SLIDER + INSTANT PLAN ────────────────────────── */}
            <div data-testid="budget-card" style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
                <div style={{ fontWeight: 700, color: T.navy, fontSize: 16 }}>2. Set your budget</div>
                <div style={{ color: T.muted, fontSize: 12 }}>Drag the slider — the plan recomputes instantly.</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 14, flexWrap: "wrap" }}>
                <input data-testid="budget-slider" type="range" min={5000} max={500000} step={1000}
                  value={brief.budget_ghs}
                  onChange={(e) => setBrief({ ...brief, budget_ghs: Number(e.target.value) })}
                  style={{ flex: 1, minWidth: 220, accentColor: BLUE }} />
                <input data-testid="budget-input" type="number" style={{ ...inp, width: 140 }}
                  value={brief.budget_ghs}
                  onChange={(e) => setBrief({ ...brief, budget_ghs: Number(e.target.value) })} />
                <div style={{ color: T.navy, fontWeight: 800, fontSize: 22, minWidth: 180 }}>
                  GH₵ {brief.budget_ghs.toLocaleString()}
                  <span style={{ color: T.muted, fontSize: 12, fontWeight: 500, marginLeft: 6 }}>total budget</span>
                </div>
              </div>

              {plan && (
                <>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginTop: 22 }}>
                    {[
                      { label: "Projected Reach", value: plan.projected_total_reach.toLocaleString(), color: T.green, sub: "people in your target" },
                      { label: "Budget Used", value: `GH₵ ${plan.budget_used_ghs.toLocaleString()}`, color: T.navy, sub: `${Math.round((plan.budget_used_ghs / brief.budget_ghs) * 100)}% of budget` },
                      { label: "Stations in Plan", value: plan.plan.length, color: BLUE, sub: `${plan.plan.reduce((s, r) => s + r.spots, 0)} total spots` },
                      { label: "Avg CPTA", value: plan.projected_total_reach ? `GH₵ ${(plan.budget_used_ghs / (plan.projected_total_reach / 1000)).toFixed(2)}` : "—", color: T.muted, sub: "per 1,000 reached" },
                    ].map((c, i) => (
                      <div key={i} data-testid={`plan-stat-${i}`} style={{ background: "#F8FAFC", borderRadius: 8, padding: 14, border: `1px solid ${T.border}` }}>
                        <div style={{ color: T.muted, fontSize: 11 }}>{c.label}</div>
                        <div style={{ color: c.color, fontSize: 22, fontWeight: 800, marginTop: 4 }}>{c.value}</div>
                        <div style={{ color: T.muted, fontSize: 10, marginTop: 2 }}>{c.sub}</div>
                      </div>
                    ))}
                  </div>

                  {plan.plan.length > 0 ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 18 }}>
                      <thead>
                        <tr style={{ color: T.muted, textAlign: "left" }}>
                          {[
                            ["Station", null],
                            ["Spots", null],
                            ["Spend", null],
                            ["Projected Reach", "target_audience"],
                            ["CPTA", "cpta"],
                          ].map(([h, term]) => (
                            <th key={h} style={{ padding: "8px 6px", borderBottom: `1px solid ${T.border}`, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {term ? <Glossary term={term}>{h}</Glossary> : h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {plan.plan.map((row, i) => (
                          <tr key={i} data-testid={`plan-row-${i}`}>
                            <td style={{ padding: "10px 6px", color: T.navy, fontWeight: 600 }}>{row.station_name}</td>
                            <td style={{ padding: "10px 6px", color: T.navy }}>{row.spots}</td>
                            <td style={{ padding: "10px 6px", color: T.navy }}>GH₵ {row.spend_ghs.toLocaleString()}</td>
                            <td style={{ padding: "10px 6px", color: T.green, fontWeight: 700 }}>{row.projected_reach.toLocaleString()}</td>
                            <td style={{ padding: "10px 6px", color: T.muted }}>GH₵ {row.cpta_ghs.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ color: T.muted, fontSize: 13, marginTop: 18, textAlign: "center" }}>
                      No slot fits your budget. Try increasing the slider above GH₵ {Math.min(...ranked.map((r) => r.rate_card_ghs)).toLocaleString()}.
                    </div>
                  )}
                </>
              )}
            </div>

            {/* ── RANKING ───────────────────────────────────────────── */}
            {ranked.length > 0 && (
              <div data-testid="ranking-card" style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 12, padding: 24, marginBottom: 24 }}>
                <div style={{ fontWeight: 700, color: T.navy, marginBottom: 4, fontSize: 16 }}>3. Top slots ranked by Value Equivalent Score</div>
                <div style={{ color: T.muted, fontSize: 12, marginBottom: 16 }}>
                  Sorted by <Glossary term="ves">VES</Glossary> (audience-fit per cedi). Click any row to add it to your plan.
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}>
                    <thead>
                      <tr style={{ color: T.muted, textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        <th style={{ padding: "8px 6px", borderBottom: `1px solid ${T.border}` }}>#</th>
                        <th style={{ padding: "8px 6px", borderBottom: `1px solid ${T.border}` }}>Station</th>
                        <th style={{ padding: "8px 6px", borderBottom: `1px solid ${T.border}` }}>Total Audience</th>
                        <th style={{ padding: "8px 6px", borderBottom: `1px solid ${T.border}` }}><Glossary term="target_audience">Target Reach</Glossary></th>
                        <th style={{ padding: "8px 6px", borderBottom: `1px solid ${T.border}` }}><Glossary term="ves">VES</Glossary></th>
                        <th style={{ padding: "8px 6px", borderBottom: `1px solid ${T.border}` }}><Glossary term="cpta">CPTA (GH₵)</Glossary></th>
                        <th style={{ padding: "8px 6px", borderBottom: `1px solid ${T.border}` }}>Rate (GH₵)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ranked.slice(0, 15).map((r, i) => (
                        <tr key={r.station_id} data-testid={`rank-row-${i}`}>
                          <td style={{ padding: "10px 6px", color: T.muted }}>{i + 1}</td>
                          <td style={{ padding: "10px 6px", color: T.navy, fontWeight: 600 }}>
                            {r.station_name} <ConfidenceLabel source={r.confidence_label} size="xs" />
                            <br /><span style={{ color: T.muted, fontSize: 11 }}>{r.frequency}</span>
                          </td>
                          <td style={{ padding: "10px 6px", color: T.navy }}>{r.estimated_total_people.toLocaleString()}</td>
                          <td style={{ padding: "10px 6px", color: BLUE, fontWeight: 700 }}>{r.target_audience.toLocaleString()}</td>
                          <td style={{ padding: "10px 6px", color: T.green, fontWeight: 700 }}>{r.ves.toLocaleString()}</td>
                          <td style={{ padding: "10px 6px", color: T.navy }}>GH₵ {r.cpta_ghs.toFixed(2)}</td>
                          <td style={{ padding: "10px 6px", color: T.muted }}>GH₵ {r.rate_card_ghs.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* ── HEATMAP ───────────────────────────────────────────── */}
            {heatmap.length > 0 && (
              <div data-testid="heatmap-card" style={{ background: "#fff", border: `1px solid ${T.border}`, borderRadius: 12, padding: 24, marginBottom: 24, overflowX: "auto" }}>
                <div style={{ fontWeight: 700, color: T.navy, marginBottom: 4, fontSize: 16 }}>4. Daypart heatmap — when your audience is listening</div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                  <div style={{ color: T.muted, fontSize: 12 }}>
                    Darker cells = stronger <Glossary term="ves">VES</Glossary> for your brief at that hour. Hover any cell for the underlying numbers.
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {[["Low", 0.05], ["Medium", 0.45], ["Best", 0.95]].map(([lbl, intensity]) => (
                      <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                        <div style={{ width: 18, height: 18, borderRadius: 3,
                          background: `rgba(37,99,235,${0.05 + intensity * 0.9})` }} />
                        <span style={{ fontSize: 10, color: T.muted }}>{lbl}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <table style={{ borderCollapse: "collapse", fontSize: 11, minWidth: "100%" }}>
                  <thead>
                    <tr>
                      <th style={{ padding: 6, textAlign: "left", color: T.muted, position: "sticky", left: 0, background: "#fff", minWidth: 160 }}>Station</th>
                      {Array.from({ length: 24 }, (_, h) => (
                        <th key={h} style={{ padding: "4px 6px", color: T.muted, fontWeight: 600 }}>{h.toString().padStart(2, "0")}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatmap.map((row) => (
                      <tr key={row.station_id}>
                        <td style={{ padding: 6, color: T.navy, fontWeight: 600, position: "sticky", left: 0, background: "#fff", borderTop: `1px solid ${T.border}` }}>
                          {row.station_name}
                        </td>
                        {row.cells.map((cell) => {
                          const intensity = cell.ves / maxVes;
                          const bg = `rgba(37,99,235,${0.05 + intensity * 0.9})`;
                          const color = intensity > 0.55 ? "#fff" : T.navy;
                          return (
                            <td key={cell.hour}
                              title={`${row.station_name} @ ${String(cell.hour).padStart(2, "0")}:00\n${cell.people.toLocaleString()} people in target\nVES ${cell.ves.toLocaleString()}`}
                              style={{ padding: "6px 4px", background: bg, color, textAlign: "center", minWidth: 28, borderTop: `1px solid ${T.border}`, cursor: "help" }}>
                              {cell.ves > 1000 ? `${(cell.ves / 1000).toFixed(0)}k` : cell.ves}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: 10, color: T.muted, fontSize: 11 }}>
                  💡 Tip: Use this to schedule spots in the cells with the highest VES for your brief — typically morning drive (06-09) and evening rush (17-20).
                </div>
              </div>
            )}
          </>
        )}

        {!hasRun && (
          <div style={{ textAlign: "center", padding: "40px 20px", background: "#F8FAFC", borderRadius: 12, color: T.muted, fontSize: 14 }}>
            👆 Adjust your audience above and click <b style={{ color: BLUE }}>Plan my buy →</b> to see your media plan.
          </div>
        )}
      </div>
    </div>
  );
}
